import * as Y from 'yjs'
import type { CitationField } from '../citation/collectClusters.js'
import { richtextFragment, rowFragment, rowsList } from '../document/sectionDocument.js'
import { COMMENT_MARK } from './CommentMark.js'

/**
 * De draad-id's waarvan het anker in de tekst van deze sectie staat, gelezen uit het document en niet
 * uit een editor (R-73).
 *
 * Het paneel Opmerkingen toont de draadjes van het hele formulier, ook van een dichtgeklapte sectie
 * waar geen editor van bestaat. `anchoredThreads` in `anchors.ts` leest een ProseMirror-document; dit
 * leest hetzelfde uit de Yjs-tekst, waar y-prosemirror een mark als attribuut met zijn naam bewaart.
 */
export function anchoredInDoc(doc: Y.Doc, fields: readonly CitationField[]): ReadonlySet<string> {
  const out = new Set<string>()
  for (const field of fields) {
    if (typeof field === 'string') {
      collect(richtextFragment(doc, field), out)
      continue
    }
    for (const row of rowsList(doc, field.herhaalgroep).toArray()) {
      for (const column of field.kolommen) {
        const fragment = rowFragment(row, column)
        if (fragment !== undefined) collect(fragment, out)
      }
    }
  }
  return out
}

function collect(node: Y.XmlFragment | Y.XmlElement | Y.XmlText | Y.XmlHook, out: Set<string>): void {
  if (node instanceof Y.XmlHook) return
  if (node instanceof Y.XmlText) {
    for (const part of node.toDelta() as { attributes?: Record<string, unknown> }[]) {
      // Overlappende marks van hetzelfde soort krijgen van y-prosemirror een sleutel `naam--hash`.
      for (const [key, mark] of Object.entries(part.attributes ?? {})) {
        if (key !== COMMENT_MARK && !key.startsWith(`${COMMENT_MARK}--`)) continue
        const id = typeof mark === 'object' && mark !== null ? (mark as { draadId?: unknown }).draadId : undefined
        if (typeof id === 'string') out.add(id)
      }
    }
    return
  }
  for (const child of node.toArray()) collect(child, out)
}
